import {
  applyFolderAccessSnapshot,
  diffFolderAccessSnapshots,
  folderAccessFromPermissions,
  getPermissionChangeDirection,
  MAX_CURVE_ALERTS_PER_FOLDER,
  type FolderAccessBaseline,
  type FolderAccessSnapshot,
  type ProductChangeAlert,
} from "../calcs/lib/features/product-alerts";

export type PermissionTableRow = {
  packageId: string;
  folderName: string | null;
  packageType?: string | null;
  permission: string;
  curves: { id: string; name: string }[];
  curveCount: number;
};

export const permissionTableSnapshot = (rows: PermissionTableRow[]): FolderAccessSnapshot => {
  const next: FolderAccessSnapshot = new Map();
  for (const row of rows) {
    next.set(row.packageId, {
      folderName: row.folderName,
      access: folderAccessFromPermissions([{ permission: row.permission }]),
      curves: row.curves.slice(0, MAX_CURVE_ALERTS_PER_FOLDER),
      curveIds: row.curves.map((curve) => curve.id),
      curveCount: row.curveCount,
    });
  }
  return next;
};

export const fingerprintFolderAccessSnapshot = (snapshot: FolderAccessSnapshot): string =>
  [...snapshot.entries()]
    .map(([packageId, entry]) => `${packageId}:${entry.access}`)
    .sort()
    .join("|");

/** Packages whose alerts name the folder and its curves: access gained or changed on a folder still present. */
export const packagesNeedingDetails = (
  lastKnown: FolderAccessBaseline,
  next: FolderAccessSnapshot,
  established: boolean,
): string[] =>
  established
    ? [...next]
        .filter(([packageId, entry]) => getPermissionChangeDirection(lastKnown.get(packageId) ?? "none", entry.access))
        .map(([packageId]) => packageId)
    : [];

export const detectPermissionTableChanges = (
  lastKnown: FolderAccessBaseline,
  next: FolderAccessSnapshot,
  established: boolean,
): ProductChangeAlert[] => {
  const alerts = diffFolderAccessSnapshots(lastKnown, next, established);
  applyFolderAccessSnapshot(lastKnown, next);
  return alerts;
};
